// Section 14: arrivals at the gate (stream E2). A group brought by an event (refugees,
// travellers, a stranger) is asked about in the next Report, or decided at once by the migration
// policy (Section 18): Open admits all, Closed turns away, Skilled only admits those who claim
// skill 3+ in a job nobody here has at 3+ (with their young children), Families only admits groups
// with children, Ask each time opens the arrival prompt. Plague "close gates" means Closed.
// Guests (fed at half rations for 2 seasons) are decided on again once their stay is over.
// The decision itself is the event's choice hook (a `gate` delta; the stranger may steal).
import { hashString } from '../../core/rng';
import type { EventChoiceDef } from '../../core/registry';
import type {
  ArrivalDecision,
  ArrivalGroup,
  EventPrompt,
  MigrationOption,
  Person,
  PersonId,
  PhaseContext,
  SkillId,
} from '../../core/types';
import { applyDeltas } from '../shared/deltas';
import { scoped } from './execute';

/** Choice ids arrival events use for each gate decision (content convention, Section 14). */
const DECISION_CHOICES: Record<ArrivalDecision, readonly string[]> = {
  'admit-all': ['admit-all', 'admit'],
  'admit-some': ['admit-some'],
  guests: ['guests'],
  'turn-away': ['turn-away', 'refuse'],
};

export function decisionOfChoice(choiceId: string): ArrivalDecision | null {
  for (const [decision, ids] of Object.entries(DECISION_CHOICES) as [
    ArrivalDecision,
    readonly string[],
  ][])
    if (ids.includes(choiceId)) return decision;
  return null;
}

function choiceFor(choices: readonly EventChoiceDef[], decision: ArrivalDecision) {
  return choices.find((c) => DECISION_CHOICES[decision].includes(c.id));
}

export function gatesClosed(ctx: PhaseContext): boolean {
  const until = ctx.draft.counters.gatesClosedUntilTurn;
  return until !== null && ctx.season.turn <= until;
}

/** Skills someone here already has at `rareSkillMin` or better (Section 14 rare skills). */
function skillsHeld(ctx: PhaseContext): Set<SkillId> {
  const held = new Set<SkillId>();
  const min = ctx.cfg.migration.rareSkillMin;
  for (const p of Object.values(ctx.draft.people))
    for (const [skill, level] of Object.entries(p.skills) as [SkillId, number][])
      if (level >= min) held.add(skill);
  return held;
}

/** The policy's decision for a group (or for guests being decided on again). */
function policyDecision(
  ctx: PhaseContext,
  group: ArrivalGroup,
  policy: Exclude<MigrationOption, 'ask'>,
  people: readonly Person[],
  followUp: boolean,
): { decision: ArrivalDecision; personIds: PersonId[] } {
  const adultAt = ctx.cfg.people.adultAtSeasons;
  const all = { decision: 'admit-all' as const, personIds: [] };
  const none = { decision: 'turn-away' as const, personIds: [] };
  switch (policy) {
    case 'open':
      return all;
    case 'closed':
      return none;
    case 'families-only':
      return people.some((p) => p.ageSeasons < adultAt) ? all : none;
    case 'skilled-only': {
      const held = skillsHeld(ctx);
      const min = ctx.cfg.migration.rareSkillMin;
      const skilled = people.filter((p) => {
        // What they claim at the gate; what they have shown after a stay as guests.
        const shown = followUp ? p.skills : (group.claimedSkills[p.id] ?? {});
        return (Object.entries(shown) as [SkillId, number][]).some(
          ([skill, level]) => level >= min && !held.has(skill),
        );
      });
      const keep = new Set(skilled.map((p) => p.id));
      for (const p of people)
        if (p.ageSeasons < adultAt && p.parentIds.some((id) => keep.has(id))) keep.add(p.id);
      if (keep.size === 0) return none;
      if (keep.size === people.length) return all;
      return { decision: 'admit-some', personIds: [...keep].sort() };
    }
  }
}

function defaultChoice(choices: readonly EventChoiceDef[], followUp: boolean): string {
  const pick = followUp
    ? choiceFor(choices, 'admit-all')
    : (choiceFor(choices, 'guests') ?? choiceFor(choices, 'turn-away'));
  return pick?.id ?? choices[0]?.id ?? 'turn-away';
}

function atTheGate(ctx: PhaseContext, group: ArrivalGroup, followUp: boolean): void {
  const d = ctx.draft;
  const def = ctx.registry.events[group.eventId ?? 'refugees'];
  const people = followUp
    ? (group.admittedIds ?? [])
        .map((id) => d.people[id])
        .filter((p): p is Person => p !== undefined)
    : group.candidates;
  const choices = def.choices.filter((c) => !followUp || decisionOfChoice(c.id) !== 'guests');
  const id = followUp ? `gate:${group.id}:again` : `gate:${group.id}`;
  const prompt: EventPrompt = {
    kind: 'event',
    id,
    turn: ctx.season.turn,
    eventId: def.id,
    params: {
      size: people.length,
      reason: group.reason,
      followUp: followUp ? 1 : 0,
      seed: hashString(`${d.seed}:${id}`),
    },
    subjectIds: people.map((p) => p.id),
    groupId: group.id,
    choices: choices.map((c) => ({ id: c.id, label: c.label })),
    defaultChoiceId: defaultChoice(choices, followUp),
  };
  if (!followUp) {
    ctx.report.add({
      kind: 'arrival',
      groupId: group.id,
      reason: group.reason,
      personIds: group.candidates.map((c) => c.id),
      decision: null,
    });
  }
  const policy = gatesClosed(ctx) ? 'closed' : d.policies.migration;
  if (policy === 'ask') {
    group.promptId = prompt.id;
    d.openPrompts.push(prompt);
    return;
  }
  let { decision, personIds } = policyDecision(ctx, group, policy, people, followUp);
  let choice = choiceFor(choices, decision);
  if (choice === undefined && decision === 'admit-some') {
    // An event without "admit some" (a lone stranger): all or nothing.
    decision = personIds.length > 0 ? 'admit-all' : 'turn-away';
    personIds = [];
    choice = choiceFor(choices, decision);
  }
  if (choice === undefined) return;
  const answer = { kind: 'event' as const, choiceId: choice.id, personIds };
  applyDeltas(scoped(ctx, promptSeed(prompt), 'choice'), choice.apply(ctx.live, prompt, answer));
}

function promptSeed(prompt: EventPrompt): number {
  const seed = prompt.params.seed;
  return typeof seed === 'number' ? seed : hashString(prompt.id);
}

/** New groups brought by this season's events: ask, or apply the migration policy. */
export function gateNewGroups(ctx: PhaseContext): void {
  const d = ctx.draft;
  for (const id of Object.keys(d.arrivalGroups).sort() as (keyof typeof d.arrivalGroups)[]) {
    const group = d.arrivalGroups[id];
    if (group === undefined || group.status !== 'pending' || group.promptId !== null) continue;
    if (group.turn !== ctx.season.turn) continue;
    atTheGate(ctx, group, false);
  }
}

/** Guests whose stay is over are decided on again (Section 14 "then decide"). */
export function guestsDecideAgain(ctx: PhaseContext): void {
  const d = ctx.draft;
  for (const id of Object.keys(d.arrivalGroups).sort() as (keyof typeof d.arrivalGroups)[]) {
    const group = d.arrivalGroups[id];
    if (group?.status !== 'guests' || group.settled === true || group.promptId !== null) continue;
    const present = (group.admittedIds ?? []).filter((pid) => d.people[pid] !== undefined);
    if (present.length === 0) {
      group.settled = true;
      continue;
    }
    if (present.some((pid) => d.people[pid]?.status.guest !== null)) continue;
    atTheGate(ctx, group, true);
  }
}

/**
 * Settled groups are kept for the Ledger's gate section only while it may still read them: a
 * decided group is dropped once its decision season is `keep` seasons old.
 */
export function pruneGroups(ctx: PhaseContext, keep: number): void {
  const d = ctx.draft;
  for (const id of Object.keys(d.arrivalGroups).sort() as (keyof typeof d.arrivalGroups)[]) {
    const g = d.arrivalGroups[id];
    if (g === undefined || g.settled !== true || g.promptId !== null) continue;
    if ((g.decidedTurn ?? g.turn) + keep <= ctx.season.turn) delete d.arrivalGroups[id];
  }
}
